import React, { CSSProperties, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    CompactSelection,
    DataEditorRef,
    DrawCellCallback,
    GridCell,
    GridCellKind,
    GridColumn,
    GridSelection,
    Item,
    ProvideEditorComponent,
} from '@glideapps/glide-data-grid';
import { renderToString } from 'react-dom/server';
import { TbLockAccess, TbTable } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useDismissibleGlideDataGridSelection } from '@/hooks/useDismissibleGlideDataGridSelection';
import { useGlideDataGridTheme } from '@/hooks/useGlideDataGridTheme';
import { ContextualCommentButton, useContextualCommentTarget } from '@/shared/components/activity';
import {
    buildFinancialGridRows,
    FINANCIAL_GROWTH_LINE_ITEM_PLACEMENTS,
} from '@/shared/components/financial-statements/revenueSegments';
import type { FinancialStatementRowGroup } from '@/shared/components/financial-statements/revenueSegments';
import { formatFinancialMetricWithDisplay, useFinancialValueDisplay } from '@/shared/components/financial-values';
import { type SparklineCell } from '@/shared/components/tables/glideCustomCells';
import { getPlanHistoricalDataYears } from '@/shared/pricing';
import { useUserStore } from '@/stores';
import { FundamentalValuationsType } from '@/utilities';
import { formatFinancialLineItemLabel } from '@/utilities/helpers/formatFinancialLineItemLabel';

import DataGridSelectionToolbar from '../DataGridSelectionToolbar';
import FinancialGlideDataGrid from '../FinancialGlideDataGrid';
import {
    createFinancialFilingHref,
    createFinancialSourceNumberCell,
    FinancialSourceAttributionButton,
    FinancialSourceAttributionPanel,
    financialValueCopyData,
    isFinancialSourceNumberCell,
    resolveFinancialFilingPathname,
    resolveFinancialSourceAttribution,
} from '../FinancialSourceAttribution';
import type { FinancialSourceAttribution, FinancialStatementKind } from '../FinancialSourceAttribution';
import GlideDataGridTableExportDropdown from '../GlideDataGridTableExportDropdown';
import { createFocusedFinancialRowSelection, matchesFinancialRowFocus } from './financialRowFocus';

interface GlideDataGridTableProps {
    company?: unknown;
    height?: CSSProperties['height'];
    aspectRatio?: number;
    data: Array<Record<string, unknown>>;
    segmentedValue: string;
    setSegmentedValue: (value: string) => void;
    statementKind?: FinancialStatementKind;
    financialRowGroups?: FinancialStatementRowGroup[];
    focusLineItem?: string;
    type?: FundamentalValuationsType;
    typeByLineItem?: Partial<Record<string, FundamentalValuationsType>>;
    schema: string[];
    loading?: boolean;
}

const selectionIndexes = (selection: GridSelection) => {
    const range = selection.current?.range;
    const rangeColumns = range ? Array.from({ length: range.width }, (_, index) => range.x + index) : [];
    const rangeRows = range ? Array.from({ length: range.height }, (_, index) => range.y + index) : [];

    return {
        columnIndexes: [...new Set([...selection.columns.toArray(), ...rangeColumns])],
        range,
        rowIndexes: [...new Set([...selection.rows.toArray(), ...rangeRows])],
    };
};

const compactSelectionFromIndexes = (indexes: number[]) =>
    indexes.reduce((selection, index) => selection.add(index), CompactSelection.empty());

const stringArray = (value: unknown) =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const isGrowthLineItem = (lineItemKey: string | undefined) =>
    Boolean(lineItemKey && (lineItemKey.startsWith('growth') || FINANCIAL_GROWTH_LINE_ITEM_PLACEMENTS[lineItemKey]));

const shouldDisplayDash = (value: unknown, type?: FundamentalValuationsType) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return true;
    }

    switch (type) {
        case FundamentalValuationsType.PERCENT:
            return false;

        case FundamentalValuationsType.NUMBER:
            return false;

        case FundamentalValuationsType.RATIO:
            return value === 0;

        case FundamentalValuationsType.VALUE:
        default:
            return false;
    }
};

const formatValue = (
    value: unknown,
    type: FundamentalValuationsType | undefined,
    financialValueDisplay: Parameters<typeof formatFinancialMetricWithDisplay>[2],
) => {
    if (shouldDisplayDash(value, type) || typeof value !== 'number') {
        return '—';
    }

    switch (type) {
        case FundamentalValuationsType.PERCENT:
            return `${(value * 100).toFixed(2)}%`;

        case FundamentalValuationsType.RATIO:
            return `${value.toFixed(2)}x`;

        case FundamentalValuationsType.NUMBER:
        case FundamentalValuationsType.VALUE:
        default:
            return formatFinancialMetricWithDisplay(value, type, financialValueDisplay);
    }
};

const reorderAndRemove = (record: Record<string, unknown>, order: string[]) => {
    const reorderedRecord: Record<string, unknown> = {};

    for (const key of order) {
        if (key in record) {
            reorderedRecord[key] = record[key];
        }
    }

    return reorderedRecord;
};

const GlideDataGridTable: FC<GlideDataGridTableProps> = ({
    company,
    data,
    financialRowGroups,
    focusLineItem,
    segmentedValue,
    height,
    statementKind,
    type,
    typeByLineItem,
    schema,
}): JSX.Element => {
    const [loading, setLoading] = useState(true);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set(['revenue']));
    const gridRef = useRef<DataEditorRef>(null);
    const handledFocusLineItemRef = useRef<string | undefined>(undefined);
    const focusedParentLineItem = useMemo(
        () =>
            focusLineItem
                ? financialRowGroups?.find((group) =>
                      group.series.some((series) => matchesFinancialRowFocus(series, focusLineItem)),
                  )?.parentLineItem
                : undefined,
        [financialRowGroups, focusLineItem],
    );
    const visibleExpandedGroups = useMemo(() => {
        if (!focusedParentLineItem || expandedGroups.has(focusedParentLineItem)) {
            return expandedGroups;
        }

        return new Set([...expandedGroups, focusedParentLineItem]);
    }, [expandedGroups, focusedParentLineItem]);

    const { token } = useAntDesignTheme();
    const { preference: financialValueDisplay } = useFinancialValueDisplay();
    const contextualCommentTarget = useContextualCommentTarget();
    const location = useLocation();
    const navigate = useNavigate();
    const { selectedUser: user } = useUserStore();
    const filingsPathname = useMemo(() => resolveFinancialFilingPathname(location.pathname), [location.pathname]);

    const handleOpenFiling = useCallback(
        (attribution: FinancialSourceAttribution) => {
            if (!filingsPathname) {
                return;
            }

            const href = createFinancialFilingHref(location.pathname, attribution, location.search);

            if (href) {
                navigate(href);
            }
        },
        [filingsPathname, location.pathname, location.search, navigate],
    );

    const lockIconRef = useRef<HTMLImageElement | null>(null);

    const lockIconSrc = useMemo(() => {
        const svg = renderToString(<TbLockAccess color={token.colorTextTertiary} size={14} />);

        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    }, [token.colorTextTertiary]);

    useEffect(() => {
        const image = new Image();
        image.src = lockIconSrc;
        lockIconRef.current = image;
    }, [lockIconSrc]);

    useEffect(() => {
        const loadingTimer = window.setTimeout(() => {
            setLoading(false);
        }, 750);

        return () => window.clearTimeout(loadingTimer);
    }, []);

    // New Logic

    const columns = useMemo(() => {
        return [
            {
                title: 'Line Items',
                id: 'lineItems',
                icon: 'TbList',
            },
            {
                title: `${segmentedValue === 'annual' ? 'YoY' : 'QoQ'} Change`,
                id: 'change',
                icon: 'TbGraph',
            },
            ...data.map(
                (item) =>
                    ({
                        title: String(item.periodLabel),
                        id: `items${String(item.periodLabel)}`,
                        icon: 'TbCalendarEvent',
                        style: 'normal',
                        hasMenu: true,
                        menuIcon: 'TbInfoCircle',
                    }) as GridColumn,
            ),
        ];
    }, [data, segmentedValue]);

    const updatedColumns = useMemo(
        () =>
            columns.map((column) => ({
                ...column,
                grow: 1,
            })),
        [columns],
    );

    const periods = useMemo(() => data.map((record) => String(record.periodLabel)), [data]);

    const historicalDataYears = getPlanHistoricalDataYears(user?.plan);

    const allowedPeriodCount = useMemo(() => {
        if (historicalDataYears === 'unlimited') {
            return periods.length;
        }

        return Math.min(periods.length, segmentedValue === 'annual' ? historicalDataYears : historicalDataYears * 4);
    }, [historicalDataYears, periods.length, segmentedValue]);

    const lockedPeriodColumnIds = useMemo(() => {
        const lockedCount = Math.max(periods.length - allowedPeriodCount, 0);

        return new Set(periods.slice(0, lockedCount).map((period) => `items${period}`));
    }, [allowedPeriodCount, periods]);

    const isHistoricalColumnLocked = useCallback(
        (columnId: string | undefined) => Boolean(columnId && lockedPeriodColumnIds.has(columnId)),
        [lockedPeriodColumnIds],
    );

    const processedDataArray = useMemo(() => {
        return data.map((record) => reorderAndRemove(record, schema));
    }, [data, schema]);

    const keys = useMemo(() => {
        return processedDataArray.length ? Object.keys(processedDataArray[0]) : [];
    }, [processedDataArray]);

    const dataByPeriod = useMemo(() => {
        return Object.fromEntries(data.map((record) => [String(record.periodLabel), record]));
    }, [data]);

    const dummyData = useMemo(
        () =>
            buildFinancialGridRows({
                data,
                expandedGroups: visibleExpandedGroups,
                keys,
                periodKind: segmentedValue === 'quarter' ? 'quarter' : 'annual',
                periods,
                financialRowGroups,
            }),
        [data, financialRowGroups, keys, periods, segmentedValue, visibleExpandedGroups],
    );

    const getCellContent = useCallback(
        (cell: Item): GridCell => {
            const [col, row] = cell;
            const dataRow = dummyData[row];

            // dumb but simple way to do this
            const indexes = columns.map((column) => String(column.id));

            const d = dataRow[indexes[col]];

            // See https://glideapps.github.io/glide-data-grid/?path=/story/extra-packages-cells--custom-cells for Custom Cells

            // For sparkline
            const valuesArray = Object.keys(dataRow)
                .filter((key) => key.startsWith('items'))
                .map((key) => Number(dataRow[key]))
                .filter((v) => !isNaN(v));

            const lineItemKey = typeof dataRow.lineItemKey === 'string' ? dataRow.lineItemKey : undefined;
            const valueType = isGrowthLineItem(lineItemKey)
                ? FundamentalValuationsType.PERCENT
                : lineItemKey
                  ? (typeByLineItem?.[lineItemKey] ?? type)
                  : type;
            const columnId = indexes[col];

            if (dataRow.isGroupHeader && col > 0) {
                return {
                    kind: GridCellKind.Text,
                    readonly: true,
                    allowOverlay: false,
                    displayData: '',
                    data: '',
                };
            }

            if (col === 1) {
                if (loading) {
                    return {
                        kind: GridCellKind.Loading,
                        allowOverlay: true,
                        skeletonHeight: 14,
                        skeletonWidth: 176,
                    };
                }

                const sparklineCell: SparklineCell = {
                    kind: GridCellKind.Custom,
                    allowOverlay: false,
                    copyData: valuesArray.join(', '),
                    data: {
                        kind: 'sparkline-cell',
                        values: valuesArray,
                        displayValues: valuesArray.map(String),
                        color: row % 2 === 0 ? token.cyan6 : token.cyan5,
                        hideAxis: true,
                        yAxis: valuesArray.length ? [Math.min(...valuesArray), Math.max(...valuesArray)] : [0, 0],
                    },
                };

                return sparklineCell;
            }

            if (!loading && col > 1 && isHistoricalColumnLocked(columnId)) {
                return {
                    kind: GridCellKind.Bubble,
                    allowOverlay: false,
                    data: ['Upgrade to Access'],
                };
            }

            if (loading) {
                return {
                    kind: GridCellKind.Loading,
                    allowOverlay: false,
                    skeletonHeight: 14,
                    skeletonWidth: 180,
                };
            }

            if (col === 0) {
                const lineItemLabel = lineItemKey
                    ? formatFinancialLineItemLabel(lineItemKey)
                    : String(dataRow.displayLabel);

                return {
                    kind: GridCellKind.Text,
                    readonly: true,
                    allowOverlay: false,
                    displayData: lineItemLabel,
                    data: lineItemLabel,
                };
            }

            const value = typeof d === 'number' ? d : undefined;
            const formattedValue = formatValue(d, valueType, financialValueDisplay);

            if (!lineItemKey || isGrowthLineItem(lineItemKey)) {
                return {
                    kind: GridCellKind.Number,
                    allowOverlay: false,
                    copyData: financialValueCopyData(value),
                    data: value,
                    displayData: formattedValue,
                    readonly: true,
                };
            }

            const period = periods[col - 2];
            const sourceRow = dataByPeriod[period] as Record<string, unknown> | undefined;
            const sourceAttribution = resolveFinancialSourceAttribution({
                formattedValue,
                lineItemKey,
                lineItemLabel: formatFinancialLineItemLabel(lineItemKey),
                periodLabel: period,
                periodKind: segmentedValue,
                row: sourceRow ?? {},
                statementKind,
                value,
            });
            return createFinancialSourceNumberCell({
                attribution: sourceAttribution,
                displayData: formattedValue,
                value,
            });
        },
        [
            columns,
            dataByPeriod,
            dummyData,
            financialValueDisplay,
            isHistoricalColumnLocked,
            loading,
            periods,
            segmentedValue,
            statementKind,
            token.cyan5,
            token.cyan6,
            type,
            typeByLineItem,
        ],
    );

    const sourceCellEditor = useCallback<ProvideEditorComponent<GridCell>>(
        ({ onFinishedEditing, value }) => {
            if (!isFinancialSourceNumberCell(value)) {
                return null;
            }

            return (
                <FinancialSourceAttributionPanel
                    attributions={[value.sourceAttribution]}
                    onOpenFiling={handleOpenFiling}
                    onRequestClose={() => onFinishedEditing(undefined, [0, 0])}
                />
            );
        },
        [handleOpenFiling],
    );
    const provideEditor = useCallback(
        (cell: GridCell) =>
            isFinancialSourceNumberCell(cell)
                ? {
                      disablePadding: true,
                      disableStyling: true,
                      editor: sourceCellEditor,
                  }
                : undefined,
        [sourceCellEditor],
    );

    // const getRowThemeOverride = useCallback(
    //     (row: any) =>
    //         row % 2 === 0
    //             ? undefined
    //             : {
    //                   bgCell: token.colorBgBase,
    //               },
    //     [],
    // );

    const [selection, setSelection] = React.useState<GridSelection>({
        columns: CompactSelection.empty(),
        rows: CompactSelection.empty(),
    });

    useEffect(() => {
        if (!focusLineItem || loading || handledFocusLineItemRef.current === focusLineItem) {
            return;
        }

        const row = dummyData.findIndex((dataRow) => matchesFinancialRowFocus(dataRow, focusLineItem));

        if (row < 0) {
            return;
        }

        const animationFrame = window.requestAnimationFrame(() => {
            setSelection(createFocusedFinancialRowSelection(row));
            handledFocusLineItemRef.current = focusLineItem;
            gridRef.current?.scrollTo(0, row, 'both', 8, 8, { hAlign: 'start', vAlign: 'center' });
        });

        return () => window.cancelAnimationFrame(animationFrame);
    }, [dummyData, focusLineItem, loading]);
    const targetSelection = contextualCommentTarget?.target.metadata?.selection as Record<string, unknown> | undefined;

    useEffect(() => {
        if (targetSelection?.kind !== 'data-grid-selection') {
            return;
        }

        const rangeValue = targetSelection.range;
        const range =
            rangeValue &&
            typeof rangeValue === 'object' &&
            'x' in rangeValue &&
            'y' in rangeValue &&
            'width' in rangeValue &&
            'height' in rangeValue &&
            [rangeValue.x, rangeValue.y, rangeValue.width, rangeValue.height].every(
                (value) => typeof value === 'number' && Number.isFinite(value),
            )
                ? {
                      height: rangeValue.height as number,
                      width: rangeValue.width as number,
                      x: rangeValue.x as number,
                      y: rangeValue.y as number,
                  }
                : undefined;
        const storedColumnIds = stringArray(targetSelection.selectedColumnIds);
        const storedRowKeys = stringArray(targetSelection.selectedRowKeys);
        const fallbackColumnLabels = stringArray(targetSelection.columns);
        const fallbackRowKeys = stringArray(targetSelection.lineItemKeys);
        const columnIndexes = (storedColumnIds.length || !range ? storedColumnIds : [])
            .map((columnId) => updatedColumns.findIndex((column) => column.id === columnId))
            .filter((index) => index >= 0);
        const labelColumnIndexes =
            columnIndexes.length || range
                ? []
                : fallbackColumnLabels
                      .map((label) => updatedColumns.findIndex((column) => column.title === label))
                      .filter((index) => index >= 0);
        const rowIndexes = (storedRowKeys.length || !range ? storedRowKeys : [])
            .map((lineItemKey) => dummyData.findIndex((row) => row.lineItems === lineItemKey))
            .filter((index) => index >= 0);
        const fallbackRowIndexes =
            rowIndexes.length || range
                ? []
                : fallbackRowKeys
                      .map((lineItemKey) => dummyData.findIndex((row) => row.lineItems === lineItemKey))
                      .filter((index) => index >= 0);
        const nextSelection: GridSelection = {
            columns: compactSelectionFromIndexes([...columnIndexes, ...labelColumnIndexes]),
            current: range
                ? {
                      cell: [range.x, range.y],
                      range,
                      rangeStack: [],
                  }
                : undefined,
            rows: compactSelectionFromIndexes([...rowIndexes, ...fallbackRowIndexes]),
        };

        queueMicrotask(() => {
            setSelection((currentSelection) => {
                const currentRange = currentSelection.current?.range;
                const rangesMatch =
                    (!currentRange && !range) ||
                    (currentRange?.x === range?.x &&
                        currentRange?.y === range?.y &&
                        currentRange?.width === range?.width &&
                        currentRange?.height === range?.height);

                return currentSelection.columns.equals(nextSelection.columns) &&
                    currentSelection.rows.equals(nextSelection.rows) &&
                    rangesMatch
                    ? currentSelection
                    : nextSelection;
            });
        });
    }, [dummyData, targetSelection, updatedColumns]);

    const selectedIndexes = useMemo(() => selectionIndexes(selection), [selection]);
    const selectedSourceAttributions = useMemo(() => {
        const selectedLineItemKey =
            selectedIndexes.rowIndexes.length === 1 ? dummyData[selectedIndexes.rowIndexes[0]]?.lineItemKey : undefined;
        const lineItemKey = typeof selectedLineItemKey === 'string' ? selectedLineItemKey : undefined;

        return selectedIndexes.columnIndexes
            .filter((columnIndex) => columnIndex > 1 && !isHistoricalColumnLocked(updatedColumns[columnIndex]?.id))
            .map((columnIndex) => {
                const period = periods[columnIndex - 2];
                const sourceRow = dataByPeriod[period] as Record<string, unknown> | undefined;
                const value =
                    lineItemKey && typeof sourceRow?.[lineItemKey] === 'number' ? sourceRow[lineItemKey] : undefined;
                const valueType = lineItemKey ? (typeByLineItem?.[lineItemKey] ?? type) : type;

                return resolveFinancialSourceAttribution({
                    formattedValue:
                        typeof value === 'number' ? formatValue(value, valueType, financialValueDisplay) : undefined,
                    lineItemKey,
                    lineItemLabel: lineItemKey ? formatFinancialLineItemLabel(lineItemKey) : undefined,
                    periodLabel: period,
                    periodKind: segmentedValue,
                    row: sourceRow ?? {},
                    statementKind,
                    value,
                });
            });
    }, [
        dataByPeriod,
        dummyData,
        financialValueDisplay,
        isHistoricalColumnLocked,
        periods,
        segmentedValue,
        statementKind,
        selectedIndexes.columnIndexes,
        selectedIndexes.rowIndexes,
        type,
        typeByLineItem,
        updatedColumns,
    ]);
    const hasSelection = selectedIndexes.columnIndexes.length > 0 || selectedIndexes.rowIndexes.length > 0;
    const selectionLabel = [
        selectedIndexes.rowIndexes.length
            ? `${selectedIndexes.rowIndexes.length} row${selectedIndexes.rowIndexes.length === 1 ? '' : 's'}`
            : undefined,
        selectedIndexes.columnIndexes.length
            ? `${selectedIndexes.columnIndexes.length} column${selectedIndexes.columnIndexes.length === 1 ? '' : 's'}`
            : undefined,
    ]
        .filter(Boolean)
        .join(', ');

    const handleSelectionChange = useCallback(
        (nextSelection: GridSelection) => {
            setSelection(nextSelection);

            if (!contextualCommentTarget) {
                return;
            }

            const { columnIndexes, range, rowIndexes } = selectionIndexes(nextSelection);

            if (!columnIndexes.length && !rowIndexes.length) {
                contextualCommentTarget.resetTarget();
                return;
            }

            const columnLabels = columnIndexes
                .map((index) => updatedColumns[index]?.title)
                .filter((label): label is string => Boolean(label));
            const columnIds = columnIndexes
                .map((index) => updatedColumns[index]?.id)
                .filter((columnId): columnId is string => Boolean(columnId));
            const rowKeys = rowIndexes
                .map((index) => dummyData[index]?.lineItems)
                .filter((lineItem): lineItem is string => typeof lineItem === 'string');
            const selectedColumnIds = nextSelection.columns
                .toArray()
                .map((index) => updatedColumns[index]?.id)
                .filter((columnId): columnId is string => Boolean(columnId));
            const selectedRowKeys = nextSelection.rows
                .toArray()
                .map((index) => dummyData[index]?.lineItems)
                .filter((lineItem): lineItem is string => typeof lineItem === 'string');
            const rowLabels = rowIndexes
                .map((index) => {
                    const row = dummyData[index];

                    return row?.lineItemKey
                        ? formatFinancialLineItemLabel(row.lineItemKey)
                        : String(row?.displayLabel ?? '');
                })
                .filter(Boolean);
            const selectionLabel = [
                rowLabels.length ? `${rowLabels.length} row${rowLabels.length === 1 ? '' : 's'}` : undefined,
                columnLabels.length
                    ? `${columnLabels.length} column${columnLabels.length === 1 ? '' : 's'}`
                    : undefined,
            ]
                .filter(Boolean)
                .join(' - ');
            const baseTarget = contextualCommentTarget.baseTarget;

            contextualCommentTarget.setTarget({
                ...baseTarget,
                label: `${baseTarget.label} - ${selectionLabel}`,
                metadata: {
                    ...(baseTarget.metadata ?? {}),
                    selection: {
                        columnIds,
                        columns: columnLabels,
                        kind: 'data-grid-selection',
                        lineItemKeys: rowKeys,
                        lineItems: rowLabels,
                        period: segmentedValue,
                        range,
                        selectedColumnIds,
                        selectedRowKeys,
                    },
                },
            });
        },
        [contextualCommentTarget, dummyData, segmentedValue, updatedColumns],
    );

    const { tableTheme } = useGlideDataGridTheme();

    const drawCell = useCallback<DrawCellCallback>(
        (args, drawContent) => {
            const columnId = updatedColumns[args.col]?.id;
            const dataRow = dummyData[args.row];

            if (!loading && args.col === 0 && dataRow) {
                const { ctx, rect } = args;
                const depth = typeof dataRow.depth === 'number' ? dataRow.depth : 0;
                const chevronX = rect.x + 10 + depth * 16;
                const centerY = rect.y + rect.height / 2;
                const lineItemLabel = dataRow.lineItemKey
                    ? formatFinancialLineItemLabel(dataRow.lineItemKey)
                    : String(dataRow.displayLabel);

                ctx.save();
                ctx.fillStyle = dataRow.isGroupHeader ? token.colorTextSecondary : token.colorText;
                ctx.font = `${dataRow.isGroupHeader ? 600 : 400} 13px ${token.fontFamily}`;
                ctx.textAlign = 'left';
                ctx.textBaseline = 'middle';

                if (dataRow.expandable && dataRow.groupKey) {
                    ctx.beginPath();
                    ctx.strokeStyle = token.colorTextTertiary;
                    ctx.lineCap = 'round';
                    ctx.lineJoin = 'round';
                    ctx.lineWidth = 1.5;

                    if (visibleExpandedGroups.has(dataRow.groupKey)) {
                        ctx.moveTo(chevronX, centerY - 2);
                        ctx.lineTo(chevronX + 4, centerY + 2);
                        ctx.lineTo(chevronX + 8, centerY - 2);
                    } else {
                        ctx.moveTo(chevronX + 2, centerY - 4);
                        ctx.lineTo(chevronX + 6, centerY);
                        ctx.lineTo(chevronX + 2, centerY + 4);
                    }

                    ctx.stroke();
                }

                ctx.fillText(lineItemLabel, chevronX + 16, centerY);
                ctx.restore();
                return;
            }

            if (loading || !isHistoricalColumnLocked(columnId)) {
                drawContent();
                return;
            }

            const { ctx, rect } = args;
            const radius = 4;

            ctx.save();
            ctx.fillStyle = token.colorFillQuaternary;
            ctx.fillRect(rect.x, rect.y, rect.width, rect.height);

            const tagWidth = Math.min(146, rect.width - 24);
            const tagHeight = 22;
            const tagX = rect.x + (rect.width - tagWidth) / 2;
            const tagY = rect.y + (rect.height - tagHeight) / 2;

            ctx.beginPath();
            ctx.roundRect(tagX, tagY, tagWidth, tagHeight, radius);
            ctx.fillStyle = token.colorBgElevated;
            ctx.fill();
            ctx.strokeStyle = token.colorBorderSecondary;
            ctx.stroke();

            const lockIcon = lockIconRef.current;
            const iconSize = 14;
            const iconX = tagX + 10;
            const iconY = rect.y + (rect.height - iconSize) / 2;

            if (lockIcon?.complete) {
                ctx.drawImage(lockIcon, iconX, iconY, iconSize, iconSize);
            }

            ctx.fillStyle = token.colorTextTertiary;
            ctx.font = `500 12px ${token.fontFamily}`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText('Upgrade to Access', iconX + iconSize + 6, rect.y + rect.height / 2);
            ctx.restore();
        },
        [
            dummyData,
            isHistoricalColumnLocked,
            loading,
            token.colorBgElevated,
            token.colorBorderSecondary,
            token.colorFillQuaternary,
            token.colorText,
            token.colorTextSecondary,
            token.colorTextTertiary,
            token.fontFamily,
            updatedColumns,
            visibleExpandedGroups,
        ],
    );
    const { clearSelection } = useDismissibleGlideDataGridSelection(
        selection,
        handleSelectionChange,
        contextualCommentTarget?.resetTarget,
    );

    const handleCellClicked = useCallback(
        (cell: Item) => {
            const [col, row] = cell;
            const columnId = updatedColumns[col]?.id;
            const groupKey = dummyData[row]?.groupKey;

            if (col === 0 && typeof groupKey === 'string') {
                setSelection({
                    columns: CompactSelection.empty(),
                    rows: CompactSelection.empty(),
                });
                contextualCommentTarget?.resetTarget();
                setExpandedGroups((current) => {
                    const next = new Set(current);

                    if (next.has(groupKey)) {
                        next.delete(groupKey);
                    } else {
                        next.add(groupKey);
                    }

                    return next;
                });
                return;
            }

            if (isHistoricalColumnLocked(columnId)) {
                navigate('/settings/account/plans/billing');
            }
        },
        [contextualCommentTarget, dummyData, isHistoricalColumnLocked, navigate, updatedColumns],
    );

    const getRowThemeOverride = useCallback(
        (row: number) =>
            dummyData[row]?.isGroupHeader
                ? {
                      bgCell: token.colorFillQuaternary,
                      bgCellMedium: token.colorFillQuaternary,
                  }
                : undefined,
        [dummyData, token.colorFillQuaternary],
    );

    return (
        <FinancialGlideDataGrid
            ref={gridRef}
            dataSelectionControl={hasSelection}
            height={height}
            drawCell={drawCell}
            getCellsForSelection={true}
            getCellContent={getCellContent}
            provideEditor={provideEditor}
            rows={dummyData.length}
            columns={updatedColumns}
            gridSelection={selection}
            onGridSelectionChange={handleSelectionChange}
            onCellClicked={handleCellClicked}
            getRowThemeOverride={getRowThemeOverride}
            rowMarkers={{
                kind: 'checkbox',
                theme: tableTheme,
                checkboxStyle: 'square',
            }}
            onPaste={true}
            columnSelect='multi'
            rowSelect='multi'
        >
            {hasSelection ? (
                <DataGridSelectionToolbar label={`${selectionLabel} Selected`} onClear={clearSelection}>
                    <FinancialSourceAttributionButton
                        attributions={selectedSourceAttributions}
                        onOpenFiling={handleOpenFiling}
                    />
                    {contextualCommentTarget?.resource ? (
                        <ContextualCommentButton
                            icon={<TbTable />}
                            resource={contextualCommentTarget.resource}
                            target={contextualCommentTarget.target}
                            tooltipPlacement='top'
                        />
                    ) : null}
                    <GlideDataGridTableExportDropdown
                        buttonStyle={{ marginRight: 0 }}
                        company={company}
                        data={data}
                        displayRows={dummyData}
                        schema={schema}
                        segmentedValue={segmentedValue}
                        selection={selection}
                    />
                </DataGridSelectionToolbar>
            ) : null}
        </FinancialGlideDataGrid>
    );
};

export default GlideDataGridTable;
